import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../services/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { XCircleIcon, SparklesIcon } from '../constants/icons';

export default function AcceptInvite() {
  const { inviteToken } = useParams();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState('');
  const [validating, setValidating] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  useEffect(() => {
    validateInvite();
  }, [inviteToken]);

  const validateInvite = async () => {
    try {
      setValidating(true);
      setError('');

      if (!inviteToken) {
        setError('No invitation token provided.');
        setInviteValid(false);
        setValidating(false);
        return;
      }

      // Decode base64url token (browser-compatible)
      // base64url uses - and _ instead of + and /
      const base64 = inviteToken.replace(/-/g, '+').replace(/_/g, '/');
      const decodedToken = atob(base64);
      const parts = decodedToken.split(':');

      if (parts.length >= 2) {
        setInviteEmail(parts[1]);
        setInviteValid(true);
      } else {
        setError('Invalid invitation link.');
        setInviteValid(false);
      }
    } catch (error) {
      console.error('Error validating invite:', error);
      setError('Invalid or expired invitation link.');
      setInviteValid(false);
    } finally {
      setValidating(false);
    }
  };

  const handleAcceptInvite = async (e) => {
    e.preventDefault();

    // Validation
    if (!displayName.trim() || displayName.trim().length < 2) {
      setError('Please enter your full name (at least 2 characters)');
      return;
    }

    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setAccepting(true);
    setError('');

    try {
      const functions = getFunctions();
      const acceptTeamInvite = httpsCallable(functions, 'acceptTeamInvite');

      const result = await acceptTeamInvite({
        inviteToken,
        displayName: displayName.trim(),
        password
      });

      // Sign in with custom token
      await signInWithCustomToken(auth, result.data.customToken);

      // Redirect to dashboard
      navigate('/generate');
    } catch (error) {
      console.error('Error accepting invite:', error);
      setError(error.message || 'Failed to accept invitation. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (!inviteValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircleIcon className="h-10 w-10 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Invalid Invitation
          </h1>
          <p className="text-gray-600 mb-6">
            {error || 'This invitation link is invalid or has expired.'}
          </p>
          <Button
            onClick={() => navigate('/login')}
            className="bg-purple-600 text-white border-0"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <SparklesIcon className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Join Team on RewordGenie
          </h1>
          <p className="text-gray-600">
            You've been invited to join the team
          </p>
          {inviteEmail && (
            <p className="text-sm text-gray-500 mt-2">
              Invitation for: <span className="font-medium">{inviteEmail}</span>
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleAcceptInvite} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <Input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your full name"
              disabled={accepting}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a password (min 6 characters)"
              disabled={accepting}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirm Password
            </label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              disabled={accepting}
              required
            />
          </div>

          <div className="pt-4">
            <Button
              type="submit"
              disabled={accepting}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0"
            >
              {accepting ? 'Creating Account...' : 'Accept Invitation & Join Team'}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            By accepting this invitation, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
